import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  ListItem,
  Text,
  Textarea,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { BannerAlert, ConfirmModal } from "@equidefi/ui";
import type React from "react";
import { useState } from "react";

interface ConfirmChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  isLoading?: boolean;
  isAccountTypeChanged: boolean;
}

export const ConfirmChangesModal: React.FC<ConfirmChangesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isAccountTypeChanged,
}: ConfirmChangesModalProps) => {
  const [reason, setReason] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitted(true);
    if (reason.trim()) {
      await onConfirm(reason);
    }
  };

  const isInvalid = isSubmitted && !reason.trim();

  return (
    <ConfirmModal
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={handleConfirm}
      title="Confirm Changes"
      confirmText="Confirm Changes"
      confirmButtonProps={{
        isDisabled: isInvalid,
        isLoading,
      }}>
      <VStack spacing={6} align="stretch">
        <Text mb="0">Are you sure you want to save these changes?</Text>
        <BannerAlert
          status="warning"
          title="If you save changes..."
          description={
            <UnorderedList ml="0" pl="4" mb="0">
              {isAccountTypeChanged && (
                <ListItem>Account type was changed thus all additional signers will be deleted.</ListItem>
              )}
              <ListItem>You may need to void and regenerate the subscription agreement.</ListItem>
              <ListItem>You may need to reissue the KYC for the investors.</ListItem>
            </UnorderedList>
          }
        />
        <FormControl isRequired isInvalid={isInvalid} mb="4">
          <FormLabel>Please provide a reason for the changes</FormLabel>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Enter reason here..." />
          {isInvalid && <FormErrorMessage>Reason is required.</FormErrorMessage>}
          <FormHelperText>The reason for the changes will be stored in the investment notes.</FormHelperText>
        </FormControl>
      </VStack>
    </ConfirmModal>
  );
};
